import { Lock, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";

interface PremiumAsset {
  title: string;
  type: string;
  required_plan: string;
  download_url?: string;
}

interface PremiumAssetBoxProps {
  asset: PremiumAsset;
  hasAccess: boolean;
  onUpgrade: () => void;
}

const planLabels: Record<string, { es: string; en: string }> = {
  sherpa: { es: "Plan Avanzado", en: "Advanced Plan" },
  fractional: { es: "Plan Profesional", en: "Professional Plan" },
  brujula: { es: "Plan Básico", en: "Basic Plan" },
};

const PremiumAssetBox = ({ asset, hasAccess, onUpgrade }: PremiumAssetBoxProps) => {
  const { lang } = useLanguage();
  const planLabel = planLabels[asset.required_plan]?.[lang] || asset.required_plan;

  if (hasAccess && asset.download_url) {
    return (
      <div className="my-6 rounded-xl border-2 border-primary/30 bg-primary/5 p-5 flex items-center gap-4">
        <Download className="w-6 h-6 text-foreground shrink-0" />
        <div className="flex-1">
          <p className="font-display font-bold text-foreground">{asset.title}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{asset.type}</p>
        </div>
        <Button asChild size="sm" className="rounded-lg">
          <a href={asset.download_url} download>
            {lang === "es" ? "Descargar" : "Download"}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="my-6 rounded-xl border-2 border-dashed border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 p-5 flex items-center gap-4">
      <Lock className="w-6 h-6 text-foreground shrink-0" />
      <div className="flex-1">
        <p className="font-display font-bold text-foreground">{asset.title}</p>
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{asset.type}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="rounded-lg border-yellow-400 text-yellow-700 hover:bg-yellow-100 whitespace-nowrap"
        onClick={onUpgrade}
      >
        {lang === "es"
          ? `Mejorar a ${planLabel} para Descargar`
          : `Upgrade to ${planLabel} to Download`}
      </Button>
    </div>
  );
};

export default PremiumAssetBox;
